import { useState, useEffect, useRef, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { auth } from "../lib/firebase";
import { db } from "../lib/db";
import ChartPrepChecklist from "../components/ChartPrepChecklist";
import RoomingBoard from "../components/RoomingBoard";
import { apiFetch } from "../lib/api";
import { MOCK_SCHEDULE, APPT_TYPES, APPT_LOCATIONS, APPT_DURATIONS, ROOMS, ROOM_COLORS, APPT_STATUS_BADGE, defaultApptForm } from "../data/mockData";
import { fmt12, timeToMinutes, todayISO } from "../utils/chartHelpers";
import { getPortalUnreadCount } from "../components/PatientInboxPage";
import { MOCK_PORTAL_INBOX } from "../components/PatientInboxPage";

const SCHEDULE_COLORS = [
  { border: "#10b981", text: "#fff", bg: "rgba(16,185,129,0.08)" },
  { border: "#4f46e5", text: "#fff", bg: "rgba(79,70,229,0.08)"  },
  { border: "#7c3aed", text: "#fff", bg: "rgba(124,58,237,0.08)" },
  { border: "#d97706", text: "#fff", bg: "rgba(217,119,6,0.08)"  },
  { border: "#ef4444", text: "#fff", bg: "rgba(239,68,68,0.08)"  },
  { border: "#06b6d4", text: "#fff", bg: "rgba(6,182,212,0.08)"  },
];

// ─── NOTIFICATION BELL ───────────────────────────────────────────────────────
export function NotificationBell({ onNavigate }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [practiceId, setPracticeId] = useState(null);
  const ref = useRef(null);

  // Load practiceId from the Identity Platform (Firebase) ID-token claims once on
  // mount. (Was supabase.auth.getSession() — null post-cutover, which silently
  // disabled the practice-scoped document/intake-form notifications.)
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    u.getIdTokenResult()
      .then(r => { if (r?.claims?.practice_id) setPracticeId(r.claims.practice_id); })
      .catch(() => {});
  }, []);

  // Load on open; also do a lightweight count on mount for badge
  useEffect(() => {
    loadNotifCount(practiceId).then(setBadgeCount);
  }, [practiceId]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    setTimeout(() => document.addEventListener("mousedown", handler), 60);
    setLoading(true);
    loadNotifications(practiceId).then(n => { setNotifs(n); setBadgeCount(n.length); setLoading(false); });
    return () => document.removeEventListener("mousedown", handler);
  }, [open, practiceId]);

  async function loadNotifCount(pid) {
    let count = 0;
    const todayStr = todayISO();
    const in30 = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    // Demo mode only: seed the badge with mock portal unread count.
    if (isDemoMode) count += getPortalUnreadCount(MOCK_PORTAL_INBOX);
    const queries = [
      db.from("messages").select("id", { count: "exact", head: true }).eq("is_read", false).then(({ count: c }) => { if (c) count += c; }),
      // Group C (2026-06-13): portal_messages flipped to Cloud SQL. db-query
      // force-ANDs practice_id from the JWT; the explicit .eq keeps the spec honest.
      (pid ? db.from("portal_messages").select("id", { count: "exact", head: true }).eq("is_read", false).eq("practice_id", pid) : db.from("portal_messages").select("id", { count: "exact", head: true }).eq("is_read", false)).then(({ count: c }) => { if (c) count += c; }),
      db.from("lab_orders").select("id", { count: "exact", head: true }).not("critical_values", "is", null).then(({ count: c }) => { if (c) count += c; }),
    ];
    if (pid) {
      queries.push(
        db.from("documents").select("id", { count: "exact", head: true }).eq("practice_id", pid).lte("expiration_date", in30).gte("expiration_date", todayStr).then(({ count: c }) => { if (c) count += c; })
      );
    }
    await Promise.allSettled(queries);
    return count;
  }

  async function loadNotifications(pid) {
    const items = [];
    const todayStr = todayISO();
    const in30 = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    const now = new Date();
    const in2h = new Date(now.getTime() + 2 * 60 * 60 * 1000);

    await Promise.allSettled([
      db.from("messages").select("id, body, sender, created_at").eq("is_read", false).limit(5)
        .then(({ data }) => (data || []).forEach(m => items.push({ id: `msg-${m.id}`, type: "message", icon: "✉️", title: m.body || "New message", sub: `From: ${m.sender || "Unknown"}`, nav: "messages" }))),
      (pid ? db.from("portal_messages").select("id, subject, sender_type, created_at").eq("is_read", false).eq("practice_id", pid).limit(5) : db.from("portal_messages").select("id, subject, sender_type, created_at").eq("is_read", false).limit(5))
        .then(({ data }) => {
          // Real portal messages only. Demo mode falls back to mock so the demo
          // looks populated; a real (empty) practice shows nothing — no fake data.
          const rows = (data && data.length > 0)
            ? data
            : (isDemoMode ? MOCK_PORTAL_INBOX.filter(m => !m.is_read && m.status !== "archived").slice(0, 5) : []);
          rows.forEach(m => items.push({ id: `pm-${m.id}`, type: "portal", icon: "📨", title: m.subject || "Portal message", sub: `From: ${m.sender_type === "patient" ? "Patient" : m.sender || "Patient"}`, nav: "patient-inbox" }));
        }),
      db.from("lab_orders").select("id, patient_id, panel_name").not("critical_values", "is", null).limit(5)
        .then(({ data }) => (data || []).forEach(l => items.push({ id: `lab-${l.id}`, type: "critical", icon: "🔴", title: `Critical: ${l.panel_name || "Lab result"}`, sub: `Patient ID: ${l.patient_id}`, nav: "labs" }))),
      ...(pid ? [db.from("documents").select("id, document_type, expiration_date").eq("practice_id", pid).lte("expiration_date", in30).gte("expiration_date", todayStr).limit(5)
        .then(({ data }) => (data || []).forEach(d => items.push({ id: `doc-${d.id}`, type: "expiry", icon: "⚠️", title: `Expiring: ${d.document_type || "Document"}`, sub: `Expires ${d.expiration_date}`, nav: "documents" })))] : []),
      // Group C (2026-06-13): intake_forms flipped to Cloud SQL (practice_id
      // force-ANDed by db-query; explicit .eq when pid is known).
      (pid ? db.from("intake_forms").select("id, patient_id, form_type").eq("status", "pending").eq("practice_id", pid).limit(5) : db.from("intake_forms").select("id, patient_id, form_type").eq("status", "pending").limit(5))
        .then(({ data }) => (data || []).forEach(f => items.push({ id: `form-${f.id}`, type: "form", icon: "📋", title: `Pending form: ${f.form_type || "Intake"}`, sub: `Patient ID: ${f.patient_id}`, nav: "portal" }))),
      db.from("appointments").select("id, patient_id, appointment_type, appointment_time").eq("appointment_date", todayStr).eq("status", "scheduled").limit(20)
        .then(({ data }) => (data || []).forEach(a => {
          if (!a.appointment_time) return;
          const [h, m] = a.appointment_time.split(":").map(Number);
          const apptTime = new Date(); apptTime.setHours(h, m, 0, 0);
          if (apptTime > now && apptTime <= in2h) {
            const mins = Math.round((apptTime - now) / 60000);
            items.push({ id: `upcoming-${a.id}`, type: "upcoming", icon: "📅", title: `${a.appointment_type || "Appt"} in ${mins} min`, sub: `Patient appt at ${a.appointment_time}`, nav: "schedule" });
          }
        })),
    ]);

    const order = { critical: 0, upcoming: 1, message: 2, portal: 2, expiry: 3, form: 4 };
    items.sort((a, b) => (order[a.type] ?? 5) - (order[b.type] ?? 5));
    return items;
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button className="btn btn-secondary btn-sm btn-icon" title="Notifications"
        onClick={() => setOpen(o => !o)} style={{ position: "relative" }}>
        🔔
        {badgeCount > 0 && (
          <span style={{ position: "absolute", top: -5, right: -5, background: "var(--red, #dc2626)", color: "#fff", borderRadius: "50%", minWidth: 16, height: 16, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, padding: "0 3px" }}>
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 340, background: "var(--navy-2)", border: "1px solid var(--border-2)", borderRadius: "var(--radius-lg)", boxShadow: "0 16px 48px rgba(0,0,0,0.4)", zIndex: 200, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Notifications</span>
            {notifs.length > 0 && <span className="badge badge-red">{notifs.length} new</span>}
          </div>
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {loading && <div style={{ padding: 24, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>Loading…</div>}
            {!loading && notifs.length === 0 && (
              <div style={{ padding: 28, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>✓</div>All caught up
              </div>
            )}
            {notifs.map(n => (
              <div key={n.id} className="tr-clickable" style={{ padding: "11px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 10 }}
                onClick={() => { onNavigate(n.nav); setOpen(false); }}>
                <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{n.icon}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)", marginBottom: 2 }}>{n.title}</div>
                  <div style={{ fontSize: 12, color: "var(--text-3)" }}>{n.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)" }}>
            <button className="btn btn-ghost btn-sm" style={{ width: "100%", justifyContent: "center" }}
              onClick={() => { setNotifs([]); setBadgeCount(0); setOpen(false); }}>
              Dismiss all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MINI CALENDAR PICKER ────────────────────────────────────────────────────
export function MiniCalendar({ currentDate, onSelect, onClose }) {
  const [viewDate, setViewDate] = useState(() => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
  const [markedDates, setMarkedDates] = useState(new Set());
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  useEffect(() => {
    const y = String(year);
    const m = String(month + 1).padStart(2, "0");
    db.from("appointments").select("appointment_date")
      .gte("appointment_date", `${y}-${m}-01`)
      .lte("appointment_date", `${y}-${m}-31`)
      .then(({ data }) => { if (data) setMarkedDates(new Set(data.map(a => a.appointment_date))); });
  }, [year, month]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const todayStr = todayISO();
  const selectedStr = currentDate.toISOString().split("T")[0];
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthName = viewDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9990 }} onClick={onClose}>
      <div style={{
        position: "absolute", top: 58, right: 8, width: 256,
        background: "var(--navy-2)", border: "1px solid var(--border)",
        borderRadius: "var(--radius)", boxShadow: "0 8px 32px rgba(0,0,0,0.5)", padding: 12,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setViewDate(new Date(year, month - 1, 1))}>‹</button>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{monthName}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setViewDate(new Date(year, month + 1, 1))}>›</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, textAlign: "center" }}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
            <div key={d} style={{ fontSize: 10, fontWeight: 600, color: "var(--text-3)", paddingBottom: 4 }}>{d}</div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`e${i}`} />;
            const ds = `${year}-${String(month+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const isToday = ds === todayStr;
            const isSel = ds === selectedStr;
            const hasDot = markedDates.has(ds);
            return (
              <div key={day} onClick={() => { onSelect(new Date(year, month, day)); onClose(); }}
                style={{
                  padding: "4px 2px", borderRadius: 4, cursor: "pointer", position: "relative",
                  background: isSel ? "var(--teal)" : isToday ? "rgba(13,148,136,0.2)" : "transparent",
                  color: isSel ? "#fff" : isToday ? "var(--teal)" : "var(--text-1)",
                  fontWeight: (isToday || isSel) ? 700 : 400, fontSize: 12,
                }}>
                {day}
                {hasDot && <span style={{ position: "absolute", bottom: 1, left: "50%", transform: "translateX(-50%)", width: 3, height: 3, borderRadius: "50%", background: isSel ? "#fff" : "var(--teal)", display: "block" }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── SCHEDULE CHECK-IN MODAL (room + vitals) ─────────────────────────────────
export function ScheduleCheckInModal({ appt, onClose, onComplete }) {
  const [room, setRoom] = useState(appt.room || "");
  const [bp, setBp] = useState(appt.vital_bp || "");
  const [hr, setHr] = useState(appt.vital_hr || "");
  const [temp, setTemp] = useState(appt.vital_temp || "");
  const [weight, setWeight] = useState(appt.vital_weight || "");
  const [saving, setSaving] = useState(false);
  const checkinTime = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const handleComplete = async () => {
    setSaving(true);
    const updates = {
      status: "checked-in", room,
      checked_in_time: new Date().toISOString(),
      vital_bp: bp, vital_hr: hr, vital_temp: temp, vital_weight: weight,
    };
    await db.from("appointments").update(updates).eq("id", appt.id);
    onComplete({ ...appt, ...updates });
    setSaving(false);
    onClose();
  };

  return createPortal(
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div className="row">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Check-In Patient</span>
            <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>PATIENT</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{appt.patient_name || "Unknown"}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>APPT TYPE</div>
              <div style={{ fontSize: 13 }}>{appt.appointment_type || appt.type}</div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>CHECK-IN TIME</div>
              <div style={{ fontSize: 13, color: "var(--teal)", fontWeight: 600 }}>{checkinTime}</div>
            </div>
            <div className="field">
              <label className="field-label">Room</label>
              <select className="input" value={room} onChange={e => setRoom(e.target.value)}>
                <option value="">— Select Room —</option>
                {ROOMS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10, color: "var(--text-2)" }}>VITALS</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field">
                <label className="field-label">Blood Pressure</label>
                <input className="input" placeholder="120/80" value={bp} onChange={e => setBp(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Heart Rate</label>
                <input className="input" placeholder="72 bpm" value={hr} onChange={e => setHr(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Temperature</label>
                <input className="input" placeholder="98.6 °F" value={temp} onChange={e => setTemp(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">Weight</label>
                <input className="input" placeholder="lbs" value={weight} onChange={e => setWeight(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleComplete} disabled={saving || !room}>
            {saving ? "Saving…" : "Complete Check-In"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── ELIGIBILITY MINI PANEL (schedule) ───────────────────────────────────────
export function EligMiniPanel({ appt, onClose }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch("eligibility", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ patientId: appt.patient.id, memberId: appt.patient.memberId, payer: appt.patient.insurance, groupNumber: "GRP-001" }),
    })
      .then(r => r.json())
      .then(data => { setResult(data); setLoading(false); })
      .catch(() => { setResult({ status: "error" }); setLoading(false); });
  }, [appt]);

  return (
    <div className="cmd-overlay" onClick={onClose}>
      <div className="cmd-modal slide-in" style={{ width: 480, maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>🛡</span>
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{appt.patient.first} {appt.patient.last} — Eligibility</div>
            <div style={{ fontSize: 11, color: "var(--text-3)" }}>{appt.patient.insurance} · {appt.patient.memberId}</div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={onClose}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: 20, flex: 1 }}>
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "24px 0", color: "var(--text-3)" }}>
              <div style={{ width: 32, height: 32, border: "2px solid var(--navy-3)", borderTopColor: "var(--teal)", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
              <div style={{ fontSize: 12 }}>Contacting clearinghouse…</div>
            </div>
          )}
          {result && !loading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Status bar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "var(--navy-3)", borderRadius: 8, border: `1px solid ${result.status === "active" ? "rgba(34,197,94,0.3)" : "rgba(224,82,82,0.3)"}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 20, color: result.status === "active" ? "var(--green)" : "var(--red)" }}>{result.status === "active" ? "✓" : "✗"}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: result.status === "active" ? "var(--green)" : "var(--red)" }}>{result.status?.toUpperCase()}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{result.planName}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 10, color: "var(--text-3)" }}>Effective</div>
                  <div style={{ fontSize: 12 }}>{result.effectiveDate} – {result.terminationDate}</div>
                </div>
              </div>
              {/* Copay + deductible grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[
                  ["Specialist Copay", `$${result.copay?.specialist ?? "—"}`],
                  ["Deductible Met", `$${result.deductible?.individualMet ?? 0} / $${result.deductible?.individual ?? 0}`],
                  ["OOP Max Met", `$${result.outOfPocketMax?.individualMet ?? 0} / $${result.outOfPocketMax?.individual ?? 0}`],
                ].map(([l, v]) => (
                  <div key={l} style={{ padding: "10px 12px", background: "var(--navy-3)", borderRadius: 6 }}>
                    <div style={{ fontSize: 10, color: "var(--text-3)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{l}</div>
                    <div style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--text-1)" }}>{v}</div>
                  </div>
                ))}
              </div>
              {/* Prior auth warning */}
              {result.priorAuth?.required && (
                <div style={{ padding: "10px 12px", background: "rgba(224,82,82,0.08)", border: "1px solid rgba(224,82,82,0.2)", borderRadius: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--red)", marginBottom: 6 }}>⚠ Prior Authorization Required</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {result.priorAuth.services.map(s => (
                      <span key={s} style={{ background: "rgba(224,82,82,0.15)", color: "var(--red)", border: "1px solid rgba(224,82,82,0.25)", padding: "2px 8px", borderRadius: 4, fontSize: 11 }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 10, color: "var(--text-3)", fontFamily: "var(--mono)" }}>ID: {result.verificationId}</div>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── SCHEDULE PAGE ────────────────────────────────────────────────────────────
export default function SchedulePage({ onPatientSelect, onCheckin, patients = [] }) {
  const hours = Array.from({ length: 10 }, (_, i) => i + 7);
  const SLOT_H = 60; // px per hour
  const HEADER_H = 36; // px for provider column header
  const typeClass = {
    "New Patient": "appt-type-new", "Post-Op": "appt-type-postop",
    "Follow-Up": "appt-type-followup", "Procedure": "appt-type-procedure",
    "Injection": "appt-type-procedure", "Urgent": "appt-type-new",
  };

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [scheduleAppts, setScheduleAppts] = useState(null);
  const [eligStatus] = useState({ P001: "verified", P002: "pending", P003: "pending", P004: "pending", P005: "pending" });
  const [miniPanel, setMiniPanel] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(defaultApptForm);
  const [ptSearch, setPtSearch] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState(["All"]);
  const [dragAppt, setDragAppt] = useState(null);
  const [dragOverInfo, setDragOverInfo] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [checkInAppt, setCheckInAppt] = useState(null);
  // ── chart prep state ────────────────────────────────────────────────────────
  const [scheduleView, setScheduleView] = useState("schedule"); // "schedule" | "chart-prep"
  const [prepStatusMap, setPrepStatusMap] = useState({});       // { appt_id: checklistRow }
  const [openPrepApptId, setOpenPrepApptId] = useState(null);   // which checklist is open in prep view
  const [practiceId, setPracticeId] = useState(null);
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

  const dateStr = currentDate.toISOString().split("T")[0];
  const todayStr = todayISO();

  // Load practiceId once from the Identity Platform (Firebase) ID-token claims.
  // (Was supabase.auth.getSession() — null post-cutover, leaving practiceId unset
  // so the practice-scoped schedule queries below never ran.)
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    u.getIdTokenResult()
      .then(r => { if (r?.claims?.practice_id) setPracticeId(r.claims.practice_id); })
      .catch(() => {});
  }, []);

  const [scheduleProviders, setScheduleProviders] = useState([]);

  useEffect(() => {
    if (!practiceId) return;
    db
      .from('providers')
      .select('id, first_name, last_name, role')
      .eq('practice_id', practiceId)
      .eq('status', 'active')
      .in('role', ['provider', 'admin'])
      .order('last_name', { ascending: true })
      .then(({ data, error }) => {
        if (error) { console.error('[schedule] providers load error:', error); return; }
        setScheduleProviders((data || []).map(p => {
          const lastName = p.last_name?.trim();
          const firstName = p.first_name?.trim();
          if (lastName) return `Dr. ${lastName}`;
          if (firstName) return `Dr. ${firstName}`;
          return 'Unknown Provider';
        }));
      });
  }, [practiceId]);

  const getProviderColor = (providerName) => {
    const idx = scheduleProviders.indexOf(providerName);
    return SCHEDULE_COLORS[idx >= 0 ? idx % SCHEDULE_COLORS.length : 0];
  };

  // Batch-fetch prep statuses when appointments load
  useEffect(() => {
    if (!scheduleAppts?.length) return;
    const ids = scheduleAppts
      .map(a => a.id)
      .filter(id => typeof id === "string" && /^[0-9a-f-]{36}$/.test(id));
    if (!ids.length) return;
    db
      .from("chart_prep_checklists")
      .select("appointment_id, status, completed_count, total_items, required_missing, ma_name, signed_off_at")
      .in("appointment_id", ids)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(r => { map[r.appointment_id] = r; });
        setPrepStatusMap(map);
      });
  }, [scheduleAppts]);

  useEffect(() => {
    setScheduleAppts(null);
    db.from("appointments")
      .select("*")
      .eq("appointment_date", dateStr)
      .order("appointment_time", { ascending: true })
      .then(({ data, error }) => {
        if (error || !data || data.length === 0) {
          if (dateStr === todayStr && isDemoMode) {
            setScheduleAppts(MOCK_SCHEDULE.map(a => ({
              id: a.id, patient_id: a.patient.id, patient: a.patient,
              patient_name: `${a.patient.first} ${a.patient.last}`,
              appointment_type: a.type, provider: a.provider,
              appointment_date: todayStr, appointment_time: a.time,
              duration: a.duration, location: "Office", status: a.status,
              room: a.room, notes: "",
            })));
          } else { setScheduleAppts([]); }
        } else {
          setScheduleAppts(data.map(a => {
            const dbFirst = a.patients?.first_name;
            const dbLast  = a.patients?.last_name;
            const propPt  = patients.find(p => p.id === a.patient_id);
            const patient_name =
              (dbFirst && dbLast)               ? `${dbFirst} ${dbLast}` :
              (propPt?.first_name && propPt?.last_name) ? `${propPt.first_name} ${propPt.last_name}` :
              "Unknown";
            return { ...a, patient: propPt || null, patient_name };
          }));
        }
      });
  }, [dateStr]);

  const goToDate = (delta) => setCurrentDate(d => { const n = new Date(d); n.setDate(n.getDate() + delta); return n; });

  const openAddModal = () => { setAddForm({ ...defaultApptForm(), appointment_date: dateStr }); setPtSearch(""); setShowAddModal(true); };

  const handleAddSave = async () => {
    if (!addForm.patient_id) return;
    setAddSaving(true);
    const payload = { ...addForm, status: "scheduled" };
    const { data, error } = await db.from("appointments").insert([payload]).select().single();
    const saved = (error || !data) ? { id: `local-${Date.now()}`, ...payload } : data;
    const pt = patients.find(p => p.id === addForm.patient_id);
    const enriched = { ...saved, patient: pt || null, patient_name: pt ? `${pt.first_name} ${pt.last_name}` : addForm.patient_id };
    if (addForm.appointment_date === dateStr) {
      setScheduleAppts(prev => [...(prev || []), enriched].sort((a, b) => (a.appointment_time || "").localeCompare(b.appointment_time || "")));
    }
    setAddSaving(false);
    setShowAddModal(false);
  };

  const shieldProps = {
    verified: { title: "Eligibility verified today", color: "var(--green)" },
    pending:  { title: "Eligibility not verified",   color: "var(--amber)" },
    inactive: { title: "Inactive coverage",           color: "var(--red)"   },
  };

  const filteredPts = ptSearch.length >= 1
    ? patients.filter(p => `${p.first_name} ${p.last_name} ${p.mrn || ''}`.toLowerCase().includes(ptSearch.toLowerCase()))
    : patients;

  const fmtDateHeader = (d) => d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  // Provider filter
  const toggleProvider = (p) => {
    if (p === "All") { setSelectedProviders(["All"]); return; }
    setSelectedProviders(prev => {
      const next = prev.filter(x => x !== "All");
      if (next.includes(p)) { const after = next.filter(x => x !== p); return after.length ? after : ["All"]; }
      return [...next, p];
    });
  };
  const isAllSelected = selectedProviders.includes("All");
  const activeProviders = isAllSelected ? scheduleProviders : scheduleProviders.filter(p => selectedProviders.includes(p));

  // Drag helpers
  const getTimeFromY = (colEl, clientY) => {
    const rect = colEl.getBoundingClientRect();
    const relY = Math.max(0, clientY - rect.top - HEADER_H);
    const snapMins = Math.round(relY / (SLOT_H / 2)) * 30; // snap 30-min
    const totalMins = 7 * 60 + snapMins;
    const h = Math.min(Math.floor(totalMins / 60), 17);
    const m = totalMins % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const handleDragStart = (appt) => setDragAppt(appt);
  const handleDragEnd = () => { setDragAppt(null); setDragOverInfo(null); };

  const confirmMove = async () => {
    const { appt, newTime, newProvider } = moveConfirm;
    const updates = { appointment_time: newTime, provider: newProvider };
    await db.from("appointments").update(updates).eq("id", appt.id);
    setScheduleAppts(prev => (prev || []).map(a => a.id === appt.id ? { ...a, ...updates } : a));
    setMoveConfirm(null);
  };

  const handleCheckInComplete = (updatedAppt) => {
    setScheduleAppts(prev => (prev || []).map(a => a.id === updatedAppt.id ? updatedAppt : a));
  };

  // Map appointment_type → visit type id for ChartPrepChecklist
  const apptTypeToVisitType = (apptType) => {
    const t = (apptType || "").toLowerCase().replace(/[- ]/g, "");
    if (t.includes("newpatient") || t.includes("newpt")) return "new_patient";
    if (t.includes("postop"))    return "postop_2wk";
    if (t.includes("preop"))     return "preop";
    if (t.includes("consult"))   return "surgical_consult";
    return "followup";
  };

  const prepDot = (apptId) => {
    const row = prepStatusMap[apptId];
    if (!row || row.status === "pending") return null;
    const isComplete = row.status === "complete";
    const hasMissing = row.required_missing > 0 && row.signed_off_at;
    const color = isComplete ? "var(--green, #22c55e)" : hasMissing ? "var(--amber, #f59e0b)" : "var(--amber, #f59e0b)";
    const title = isComplete
      ? `Chart ready · ${row.ma_name || ""}`
      : hasMissing
      ? `Missing ${row.required_missing} required items`
      : "Prep in progress";
    return (
      <span
        title={title}
        style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block", flexShrink: 0, marginTop: 3 }}
      />
    );
  };

  const renderApptCard = (appt, pc) => {
    const startMin = timeToMinutes(appt.appointment_time || appt.time || "08:00");
    const topOffset = (startMin - 7 * 60) + HEADER_H;
    const height = Math.max(appt.duration || 30, 56); // min 56px so name + meta + badge all fit
    const elig = eligStatus[appt.patient_id] || "pending";
    const shield = shieldProps[elig];
    const provColor = pc || getProviderColor(appt.provider);
    const roomColor = ROOM_COLORS[appt.room] || "var(--text-3)";
    const checkedIn = appt.status === "checked-in";
    return (
      <div key={appt.id}
        className={`appt-block ${typeClass[appt.appointment_type || appt.type] || "appt-type-followup"}`}
        draggable onDragStart={() => handleDragStart(appt)} onDragEnd={handleDragEnd}
        style={{ top: topOffset, height, cursor: "grab", borderLeft: `3px solid ${provColor.border}`, overflow: "hidden" }}
        onClick={() => appt.patient && onPatientSelect(appt.patient)}
      >
        {/* Row 1: patient name + prep dot + eligibility shield */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4, marginBottom: 2 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            {appt.patient_name || "Unknown"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {prepDot(appt.id)}
            <button title={shield.title} onClick={e => { e.stopPropagation(); setMiniPanel(appt); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 11, color: shield.color, lineHeight: 1 }}>🛡</button>
          </div>
        </div>

        {/* Row 2: time · type */}
        <div style={{ fontSize: 10, color: "var(--text-2)", lineHeight: 1.3, marginBottom: 3 }}>
          {appt.appointment_time || appt.time} · {appt.appointment_type || appt.type}
          {appt.room && (
            <span style={{ marginLeft: 5 }}>
              <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: roomColor, verticalAlign: "middle", marginRight: 2 }} />
              {appt.room}
            </span>
          )}
        </div>

        {/* Row 3: status badge or Check In button */}
        <div>
          {checkedIn
            ? <span className="badge badge-green" style={{ fontSize: 9 }}>✓ Checked In</span>
            : (
              <button
                className="btn btn-primary"
                style={{ fontSize: 9, padding: "2px 7px", height: "auto", lineHeight: 1.4 }}
                onClick={e => { e.stopPropagation(); setCheckInAppt(appt); }}
              >
                Check In
              </button>
            )
          }
        </div>
      </div>
    );
  };

  return (
    <div className="slide-in">
      {/* Header */}
      <div className="row mb-12">
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Schedule</div>
          <div className="text-muted text-sm">{fmtDateHeader(currentDate)}</div>
        </div>
        <div className="ml-auto row gap-8" style={{ position: "relative" }}>
          {/* View toggle */}
          <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
            {[{ id: "schedule", label: "Schedule" }, { id: "board", label: "Board" }, { id: "chart-prep", label: "Chart prep" }].map(v => (
              <button key={v.id} onClick={() => setScheduleView(v.id)} style={{
                padding: "4px 12px", fontSize: 12, cursor: "pointer", border: "none",
                background: scheduleView === v.id ? "var(--teal)" : "transparent",
                color: scheduleView === v.id ? "#fff" : "var(--text-2)",
                fontWeight: scheduleView === v.id ? 600 : 400, fontFamily: "inherit",
              }}>{v.label}</button>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => goToDate(-1)}>← Prev</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCurrentDate(new Date())}>Today</button>
          <button className="btn btn-secondary btn-sm" onClick={() => goToDate(1)}>Next →</button>
          <button className="btn btn-secondary btn-sm" title="Open calendar picker" onClick={() => setShowCalendar(c => !c)}>📅</button>
          <button className="btn btn-primary btn-sm" onClick={openAddModal}>+ Add Appointment</button>
          {showCalendar && <MiniCalendar currentDate={currentDate} onSelect={d => { setCurrentDate(d); setShowCalendar(false); }} onClose={() => setShowCalendar(false)} />}
        </div>
      </div>

      {/* Provider filter pills */}
      <div className="row gap-6 mb-16" style={{ flexWrap: "wrap" }}>
        <button onClick={() => toggleProvider("All")} style={{
          padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
          border: `1px solid ${isAllSelected ? "var(--teal)" : "var(--border)"}`,
          background: isAllSelected ? "rgba(13,148,136,0.15)" : "transparent",
          color: isAllSelected ? "var(--teal)" : "var(--text-1)", fontWeight: isAllSelected ? 600 : 400,
        }}>All Providers</button>
        {scheduleProviders.map(p => {
          const pc = getProviderColor(p);
          const active = !isAllSelected && selectedProviders.includes(p);
          return (
            <button key={p} onClick={() => toggleProvider(p)} style={{
              padding: "4px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
              border: `1px solid ${active ? pc.border : "var(--border)"}`,
              background: active ? `${pc.border}22` : "transparent",
              color: active ? pc.border : "var(--text-1)", fontWeight: active ? 600 : 400,
            }}>{p}</button>
          );
        })}
      </div>

      {/* ── Chart Prep list view ─────────────────────────────────────────── */}
      {scheduleView === "chart-prep" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {/* List header */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>MA Chart Prep — {fmtDateHeader(currentDate)}</span>
            <span style={{ fontSize: 11, color: "var(--text-3)" }}>
              {scheduleAppts === null ? "Loading…" : `${scheduleAppts.length} appointment${scheduleAppts.length !== 1 ? "s" : ""}`}
            </span>
          </div>
          {scheduleAppts === null && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>Loading…</div>
          )}
          {scheduleAppts?.length === 0 && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>No appointments for this date.</div>
          )}
          {(scheduleAppts || []).map(appt => {
            const prep = prepStatusMap[appt.id];
            const isComplete   = prep?.status === "complete";
            const isInProgress = prep?.status === "in_progress";
            const isOpen = openPrepApptId === appt.id;
            const apptIdValid = typeof appt.id === "string" && /^[0-9a-f-]{36}$/.test(appt.id);
            const statusColor  = isComplete ? "var(--green, #22c55e)" : isInProgress ? "var(--amber, #f59e0b)" : "var(--text-3)";
            const statusLabel  = isComplete ? `Chart ready · ${prep.ma_name}` : isInProgress ? "Prep in progress" : "Not started";
            const btnLabel     = isComplete ? "View prep" : isInProgress ? "Continue prep" : "Start prep";
            return (
              <div key={appt.id} style={{ borderBottom: "1px solid var(--border)" }}>
                {/* Row */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>
                      {appt.patient_name || "Unknown"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                      {appt.appointment_time || "—"} · {appt.appointment_type || "Appt"} · {appt.provider || ""}
                    </div>
                  </div>
                  {/* Prep status badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, display: "inline-block" }} />
                    <span style={{ fontSize: 11, color: statusColor }}>{statusLabel}</span>
                  </div>
                  {/* Action buttons */}
                  {apptIdValid && (
                    <button
                      className={`btn btn-secondary btn-sm`}
                      onClick={() => setOpenPrepApptId(isOpen ? null : appt.id)}
                      style={{ flexShrink: 0, fontSize: 12 }}
                    >
                      {isOpen ? "Close" : btnLabel}
                    </button>
                  )}
                  {apptIdValid && onPatientSelect && (
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{ flexShrink: 0, fontSize: 12, color: "var(--teal)" }}
                      onClick={() => {
                        const nameParts = (appt.patient_name || "").split(" ");
                        const pt = appt.patient || {
                          id:         appt.patient_id,
                          first_name: nameParts[0]            || "",
                          last_name:  nameParts.slice(1).join(" ") || "",
                          mrn:        appt.mrn || "",
                        };
                        onPatientSelect(pt, "chartprep");
                      }}
                    >
                      Open in chart →
                    </button>
                  )}
                </div>
                {/* Inline checklist */}
                {isOpen && apptIdValid && (
                  <div style={{ padding: "0 16px 16px", borderTop: "1px solid var(--border)", background: "var(--navy-3)" }}>
                    <ChartPrepChecklist
                      patientId={appt.patient_id}
                      patientName={appt.patient_name}
                      appointmentId={appt.id}
                      appointmentDate={appt.appointment_date}
                      providerName={appt.provider}
                      visitType={apptTypeToVisitType(appt.appointment_type)}
                      practiceId={practiceId}
                      readOnly={false}
                      onSignOff={(row) => {
                        setPrepStatusMap(prev => ({ ...prev, [appt.id]: { ...row, appointment_id: appt.id } }));
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Rooming board */}
      {scheduleView === "board" && <RoomingBoard onPatientSelect={onPatientSelect} patients={patients} />}

      {/* Multi-column schedule */}
      {scheduleView === "schedule" && <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", overflowX: "auto" }}>
          {/* Time column */}
          <div style={{ width: 52, flexShrink: 0, borderRight: "1px solid var(--border)" }}>
            <div style={{ height: HEADER_H, borderBottom: "1px solid var(--border)" }} />
            {hours.map(h => (
              <div key={h} style={{ height: SLOT_H, borderBottom: "1px solid var(--border)", display: "flex", alignItems: "flex-start", paddingTop: 4, justifyContent: "flex-end", paddingRight: 8 }}>
                <span style={{ fontSize: 10, color: "var(--text-3)" }}>{h === 12 ? "12p" : h > 12 ? `${h-12}p` : `${h}a`}</span>
              </div>
            ))}
          </div>
          {/* Empty state when no providers configured */}
          {scheduleProviders.length === 0 && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 20px", color: "var(--text-3)", fontSize: 13 }}>
              No providers configured — add providers in Settings
            </div>
          )}
          {/* Provider columns */}
          {activeProviders.map(provider => {
            const pc = getProviderColor(provider);
            const provAppts = (scheduleAppts || []).filter(a => (a.provider || "Dr. Walker") === provider);
            const isOver = dragOverInfo?.provider === provider;
            return (
              <div key={provider} style={{ flex: 1, minWidth: 160, borderLeft: "1px solid var(--border)", position: "relative", cursor: "copy" }}
                onDragOver={e => { e.preventDefault(); setDragOverInfo({ provider, time: getTimeFromY(e.currentTarget, e.clientY) }); }}
                onDragLeave={() => setDragOverInfo(null)}
                onDrop={e => { e.preventDefault(); if (!dragAppt) return; const t = getTimeFromY(e.currentTarget, e.clientY); setMoveConfirm({ appt: dragAppt, newTime: t, newProvider: provider }); setDragAppt(null); setDragOverInfo(null); }}
                onClick={e => {
                  // Click an empty slot → Add Appointment prefilled with this
                  // provider + snapped time + current date. Ignore clicks on
                  // appointment cards (they open the chart) and the header.
                  if (e.target.closest('.appt-block')) return;
                  if (e.clientY - e.currentTarget.getBoundingClientRect().top < HEADER_H) return;
                  const t = getTimeFromY(e.currentTarget, e.clientY);
                  setAddForm({ ...defaultApptForm(), appointment_date: dateStr, appointment_time: t, provider });
                  setPtSearch("");
                  setShowAddModal(true);
                }}
              >
                {/* Provider header */}
                <div style={{ height: HEADER_H, borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", padding: "0 10px", gap: 6, background: pc.bg }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: pc.border, display: "inline-block", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: pc.border }}>{provider}</span>
                  <span className="badge badge-gray" style={{ marginLeft: "auto", fontSize: 10 }}>{provAppts.length}</span>
                </div>
                {/* Hour lines */}
                {hours.map(h => <div key={h} style={{ height: SLOT_H, borderBottom: "1px solid var(--border)" }} />)}
                {/* States */}
                {scheduleAppts === null && provider === activeProviders[0] && (
                  <div style={{ position: "absolute", top: HEADER_H + 40, left: "50%", transform: "translateX(-50%)", color: "var(--text-3)", fontSize: 12 }}>Loading…</div>
                )}
                {scheduleAppts?.length === 0 && (
                  <div style={{ position: "absolute", top: HEADER_H + 40, left: "50%", transform: "translateX(-50%)", color: "var(--text-3)", fontSize: 12, whiteSpace: "nowrap" }}>No appts</div>
                )}
                {/* Drag-over indicator */}
                {isOver && dragOverInfo && (
                  <div style={{ position: "absolute", left: 0, right: 0, top: (timeToMinutes(dragOverInfo.time) - 7 * 60) + HEADER_H, height: 2, background: "var(--teal)", zIndex: 10, pointerEvents: "none" }}>
                    <span style={{ position: "absolute", left: 4, top: -9, fontSize: 9, background: "var(--teal)", color: "#fff", padding: "1px 4px", borderRadius: 3 }}>{dragOverInfo.time}</span>
                  </div>
                )}
                {/* Cards */}
                {provAppts.map(appt => renderApptCard(appt, pc))}
              </div>
            );
          })}
        </div>
      </div>}

      {miniPanel && <EligMiniPanel appt={miniPanel} onClose={() => setMiniPanel(null)} />}
      {checkInAppt && <ScheduleCheckInModal appt={checkInAppt} onClose={() => setCheckInAppt(null)} onComplete={handleCheckInComplete} />}

      {/* Move confirmation */}
      {moveConfirm && createPortal(
        <div className="cmd-overlay" onClick={() => setMoveConfirm(null)}>
          <div className="cmd-modal" style={{ width: 360 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Move Appointment</div>
              <div style={{ fontSize: 13, color: "var(--text-2)" }}>
                Move <strong>{moveConfirm.appt.patient_name}</strong> to <strong>{moveConfirm.newTime}</strong>
                {moveConfirm.newProvider !== moveConfirm.appt.provider && <> with <strong>{moveConfirm.newProvider}</strong></>}?
              </div>
            </div>
            <div style={{ padding: "12px 24px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setMoveConfirm(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmMove}>Yes, Move</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Appointment modal */}
      {showAddModal && createPortal(
        <div className="cmd-overlay" onClick={() => setShowAddModal(false)}>
          <div className="cmd-modal" style={{ width: 500 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
              <div className="row">
                <span style={{ fontSize: 14, fontWeight: 500 }}>Add Appointment</span>
                <button className="btn btn-ghost btn-icon btn-sm ml-auto" onClick={() => setShowAddModal(false)}>✕</button>
              </div>
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="field">
                <label className="field-label">Patient</label>
                <input className="input" placeholder="Search by name or MRN…" value={ptSearch}
                  onChange={e => { setPtSearch(e.target.value); setAddForm(f => ({ ...f, patient_id: "" })); }} />
                {ptSearch.length >= 1 && !addForm.patient_id && (
                  <div style={{ background: "var(--navy-3)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginTop: 4, maxHeight: 160, overflowY: "auto" }}>
                    {filteredPts.slice(0, 8).map(p => (
                      <div key={p.id} className="tr-clickable" style={{ padding: "8px 12px", fontSize: 13, display: "flex", gap: 8 }}
                        onClick={() => { setPtSearch(`${p.first_name} ${p.last_name}`); setAddForm(f => ({ ...f, patient_id: p.id })); }}>
                        <span className="fw-500">{p.first_name} {p.last_name}</span><span className="text-muted">{p.mrn}</span>
                      </div>
                    ))}
                    {filteredPts.length === 0 && <div style={{ padding: "8px 12px", fontSize: 13, color: "var(--text-3)" }}>No patients found</div>}
                  </div>
                )}
                {addForm.patient_id && (
                  <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="badge badge-teal">{ptSearch}</span>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11 }} onClick={() => { setAddForm(f => ({ ...f, patient_id: "" })); setPtSearch(""); }}>✕ Clear</button>
                  </div>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label className="field-label">Appointment Type</label>
                  <select className="input" value={addForm.appointment_type} onChange={e => setAddForm(f => ({ ...f, appointment_type: e.target.value }))}>
                    {APPT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label className="field-label">Provider</label>
                  <select className="input" value={addForm.provider} onChange={e => setAddForm(f => ({ ...f, provider: e.target.value }))}>
                    {scheduleProviders.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label className="field-label">Date</label>
                  <input type="date" className="input" value={addForm.appointment_date} onChange={e => setAddForm(f => ({ ...f, appointment_date: e.target.value }))} />
                </div>
                <div className="field">
                  <label className="field-label">Time</label>
                  <input type="time" className="input" value={addForm.appointment_time} onChange={e => setAddForm(f => ({ ...f, appointment_time: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label className="field-label">Duration</label>
                  <select className="input" value={addForm.duration} onChange={e => setAddForm(f => ({ ...f, duration: Number(e.target.value) }))}>
                    {APPT_DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
                <div className="field">
                  <label className="field-label">Room</label>
                  <select className="input" value={addForm.room} onChange={e => setAddForm(f => ({ ...f, room: e.target.value }))}>
                    <option value="">— Select Room —</option>
                    {ROOMS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="field">
                  <label className="field-label">Location</label>
                  <select className="input" value={addForm.location} onChange={e => setAddForm(f => ({ ...f, location: e.target.value }))}>
                    {APPT_LOCATIONS.map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label className="field-label">Notes</label>
                  <input className="input" placeholder="Optional…" value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddSave} disabled={addSaving || !addForm.patient_id || !addForm.appointment_date}>
                {addSaving ? "Saving…" : "Save Appointment"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
